import React, { useState, useEffect } from 'react';
import { Card, Button } from '../common';
import {
  UploadCloud,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  HelpCircle,
  ExternalLink,
  Table,
  Check,
  Zap,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { getSetting, saveSetting } from '../../db';
import {
  validateGoogleWebAppUrl,
  testGoogleSheetsConnection,
  type GoogleConnectionTestResult,
} from '../../services/sync/googleSheetsClient';
import { syncQueueEngine } from '../../services/sync/syncQueueEngine';
import { saveAndBroadcastConfig, generateShareableSetupUrl } from '../../services/config/cloudConfigService';
import { EMBEDDED_SYSTEM_CONFIG } from '../../config/embeddedConfig';

export interface GoogleSheetsSettingsProps {
  onConfigSaved?: (url: string) => void;
  className?: string;
}

export const GoogleSheetsSettings: React.FC<GoogleSheetsSettingsProps> = ({
  onConfigSaved,
  className = '',
}) => {
  const [webAppUrl, setWebAppUrl] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<string>('');
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<GoogleConnectionTestResult | null>(null);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncFeedback, setSyncFeedback] = useState<string>('');
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [validationWarning, setValidationWarning] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Load saved settings & subscribe to queue updates on mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const storedUrl = (await getSetting<string>('gas_endpoint_url')) || '';
        const storedConfig = await getSetting<{ enabled?: boolean; webAppUrl?: string }>('gas_config');

        if (isMounted) {
          if (storedConfig?.webAppUrl) {
            setWebAppUrl(storedConfig.webAppUrl);
          } else if (storedUrl) {
            setWebAppUrl(storedUrl);
          } else {
            setWebAppUrl(EMBEDDED_SYSTEM_CONFIG.gas_endpoint_url);
          }

          if (storedConfig && typeof storedConfig.enabled === 'boolean') {
            setEnabled(storedConfig.enabled);
          }

          const count = await syncQueueEngine.getPendingCount();
          setPendingCount(count);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[GoogleSheetsSettings] Failed to load settings:', err);
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();

    // Subscribe to live queue changes
    const unsubscribe = syncQueueEngine.subscribe((count) => {
      if (isMounted) {
        setPendingCount(count);
      }
    });

    // Network status listener
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      isMounted = false;
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Real-time URL validation when user types
  const handleUrlChange = (value: string) => {
    setWebAppUrl(value);
    setTestResult(null); // Reset test result on URL change

    const validation = validateGoogleWebAppUrl(value);
    if (value.trim() && validation.warning) {
      setValidationWarning(validation.warning);
    } else {
      setValidationWarning('');
    }
  };

  // Save URL and broadcast to all devices via Server + Cloud
  const handleSaveConfig = async () => {
    const trimmed = webAppUrl.trim();
    setIsSaving(true);
    setSaveSuccess('');

    try {
      const broadcastResult = await saveAndBroadcastConfig({
        gas_endpoint_url: trimmed,
        gas_config: {
          webAppUrl: trimmed,
          enabled,
        },
      });

      setSaveSuccess(broadcastResult.message);
      if (onConfigSaved) onConfigSaved(trimmed);

      // If sync re-enabled and online, trigger background drain
      if (enabled && isOnline && trimmed) {
        syncQueueEngine.drainQueue().catch(() => {});
      }

      setTimeout(() => setSaveSuccess(''), 5000);
    } catch (err: any) {
      alert(`ไม่สามารถบันทึกการตั้งค่าได้: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Copy 1-Click setup link for other devices
  const handleCopySetupLink = async () => {
    try {
      const link = await generateShareableSetupUrl();
      await navigator.clipboard.writeText(link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (e) {
      alert('ไม่สามารถคัดลอกลิงก์ได้ กรุณาคัดลอก URL ในช่องด้านบนแทน');
    }
  };

  // Test connection to Google Apps Script Web App
  const handleTestConnection = async () => {
    const trimmed = webAppUrl.trim();
    if (!trimmed) {
      setTestResult({
        success: false,
        message: 'กรุณาระบุ Google Apps Script Web App URL ก่อนทดสอบการเชื่อมต่อ',
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await testGoogleSheetsConnection(trimmed);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: `การเชื่อมต่อล้มเหลว: ${err.message || 'Network error'}`,
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Trigger manual sync of queued offline inspections
  const handleSyncNow = async () => {
    if (!isOnline) {
      setSyncFeedback('อุปกรณ์ออฟไลน์อยู่: ไม่สามารถซิงค์ได้ในขณะนี้');
      setTimeout(() => setSyncFeedback(''), 4000);
      return;
    }

    setIsSyncing(true);
    setSyncFeedback('');

    try {
      const result = await syncQueueEngine.drainQueue();
      if (result.processed === 0) {
        setSyncFeedback('ไม่มีรายการค้างซิงค์ ข้อมูลทั้งหมดเป็นปัจจุบันแล้ว');
      } else if (result.succeeded > 0) {
        setSyncFeedback(`ซิงค์ข้อมูลสำเร็จ ${result.succeeded} จาก ${result.processed} รายการ`);
      } else {
        setSyncFeedback(`การซิงค์ล้มเหลว: ${result.errors[0] || 'เกิดข้อผิดพลาดในการเชื่อมต่อ'}`);
      }
    } catch (err: any) {
      setSyncFeedback(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncFeedback(''), 5000);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <div className="flex items-center justify-center py-8 text-slate-400 gap-2">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
          <span className="text-sm">กำลังโหลดการตั้งค่า Google Sheets...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={className}
      title={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2.5">
            <UploadCloud className="w-5 h-5 text-blue-600" />
            <span className="text-slate-900 font-bold text-base">
              การเชื่อมต่อ Google Sheets Cloud Sync
            </span>
          </div>

          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className={`w-12 h-7 rounded-full transition-colors duration-200 ease-in-out p-0.5 flex items-center ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}>
              <div
                className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </div>
            <span className="ml-2.5 text-xs font-semibold text-slate-700">
              {enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
            </span>
          </label>
        </div>
      }
      subtitle="บันทึกข้อมูลผลการตรวจเช็กและอัปเดตสถานะเครื่องจักรขึ้น Google Sheets อัตโนมัติด้วย Google Apps Script"
    >
      <div className="space-y-4">
        {/* Sync Queue & Network Status Banner */}
        <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 rounded-lg border ${
                isOnline
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-rose-50 border-rose-200 text-rose-700'
              }`}
            >
              {isOnline ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">สถานะเครือข่าย:</span>
                <span
                  className={`text-xs font-bold ${
                    isOnline ? 'text-emerald-700' : 'text-rose-700'
                  }`}
                >
                  {isOnline ? 'เชื่อมต่ออินเทอร์เน็ต (Online)' : 'ออฟไลน์ (Offline)'}
                </span>
              </div>
              <div className="text-xs text-slate-700 mt-0.5">
                รายการรอซิงค์คลาวด์:{' '}
                <span
                  className={`font-bold ${
                    pendingCount > 0 ? 'text-amber-700' : 'text-emerald-700'
                  }`}
                >
                  {pendingCount} รายการ
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {syncFeedback && (
              <span className="text-xs font-semibold text-blue-700 animate-in fade-in">
                {syncFeedback}
              </span>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleSyncNow}
              isLoading={isSyncing}
              disabled={!enabled || !isOnline}
              leftIcon={<Zap className="w-4 h-4 text-amber-600" />}
              className="w-full sm:w-auto"
            >
              ซิงค์ทันที (Sync Now)
            </Button>
          </div>
        </div>

        {/* Centralized Distribution Status Banner */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between flex-wrap gap-2 text-xs text-blue-900">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse shrink-0" />
            <div>
              <strong className="block text-blue-950 font-bold">
                ระบบกระจายการตั้งค่าแบบรวมศูนย์ (Centralized Auto-Distribution):
              </strong>
              <p className="text-[11px] text-blue-700">
                เมื่อ Admin เปลี่ยน URL ที่เครื่องนี้ ระบบจะอัปเดตไฟล์ส่วนกลางและคลาวด์ให้อัตโนมัติ ทุกเครื่องจะได้รับค่าใหม่ทันที
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCopySetupLink}
            className="px-3 py-1.5 rounded-lg bg-white hover:bg-blue-100 text-blue-800 border border-blue-300 font-semibold text-xs flex items-center gap-1.5 transition-colors shadow-2xs cursor-pointer"
            title="คัดลอกลิงก์ตั้งค่าอัตโนมัติสำหรับส่งให้ช่างใหม่"
          >
            {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <ExternalLink className="w-3.5 h-3.5 text-blue-600" />}
            <span>{copiedLink ? 'คัดลอกลิงก์สำเร็จ!' : 'คัดลอก 1-Click Setup Link'}</span>
          </button>
        </div>

        {/* Web App URL Input */}
        <div>
          <label
            htmlFor="gas-web-app-url-input"
            className="block text-xs font-semibold text-slate-700 mb-1.5"
          >
            Google Apps Script Web App URL (doPost Endpoint)
          </label>
          <input
            id="gas-web-app-url-input"
            type="url"
            value={webAppUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            onBlur={() => handleUrlChange(webAppUrl.trim())}
            placeholder="https://script.google.com/macros/s/AKfycb.../exec"
            className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none min-h-[48px] placeholder-slate-400"
          />

          {validationWarning && (
            <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span>{validationWarning}</span>
            </p>
          )}

          <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1">
            <span>
              คัดลอก Web App URL จาก Apps Script (สิทธิ์: Anyone can access)
            </span>
            <span className="text-slate-400 hidden sm:inline">
              ไฟล์ Code.gs อยู่ในโฟลเดอร์ gas/Code.gs
            </span>
          </div>
        </div>

        {/* Real-time Test Result Banner */}
        {testResult && (
          <div
            className={`p-3.5 rounded-xl border text-xs animate-in fade-in ${
              testResult.success
                ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                : 'bg-rose-50 border-rose-300 text-rose-800'
            }`}
          >
            <div className="flex items-start gap-2">
              {testResult.success ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1">
                <p className="font-semibold">{testResult.message}</p>
                {testResult.sheetsFound && testResult.sheetsFound.length > 0 && (
                  <div className="flex items-center gap-2 text-[11px] opacity-90">
                    <Table className="w-3.5 h-3.5" />
                    <span>
                      พบชีตในระบบ:{' '}
                      {testResult.sheetsFound.join(', ')}
                    </span>
                  </div>
                )}
                {testResult.spreadsheetId && (
                  <p className="text-[10px] font-mono opacity-75">
                    Spreadsheet ID: {testResult.spreadsheetId}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Apps Script Setup Instructions Modal / Box */}
        <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 text-[11px] text-slate-600 space-y-1">
          <div className="flex items-center gap-1.5 font-semibold text-slate-800">
            <HelpCircle className="w-3.5 h-3.5 text-blue-600" />
            <span>ขั้นตอนการติดตั้ง Google Apps Script (`gas/Code.gs`):</span>
          </div>
          <ol className="list-decimal list-inside space-y-0.5 pl-1">
            <li>เปิด Google Sheets ที่ต้องการใช้งาน $\rightarrow$ Extensions $\rightarrow$ Apps Script</li>
            <li>คัดลอกโค้ดทั้งหมดจากไฟล์ <code className="text-blue-700 font-mono bg-blue-50 px-1 py-0.5 rounded border border-blue-200">gas/Code.gs</code> ไปวางแทนที่ใน Editor</li>
            <li>กด Deploy $\rightarrow$ New deployment $\rightarrow$ เลือก Web app</li>
            <li>กำหนด Execute as: <strong>Me</strong> และ Who has access: <strong>Anyone</strong></li>
            <li>คัดลอก Web App URL ที่ได้มาวางในช่องด้านบนนี้</li>
          </ol>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-slate-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleTestConnection}
            isLoading={isTesting}
            leftIcon={<RefreshCw className="w-4 h-4 text-blue-600" />}
            className="w-full sm:w-auto"
          >
            ทดสอบการเชื่อมต่อ (Test Connection)
          </Button>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            {saveSuccess && (
              <span className="text-xs font-bold text-emerald-700 animate-in fade-in flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                {saveSuccess}
              </span>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveConfig}
              isLoading={isSaving}
              leftIcon={<CheckCircle2 className="w-4 h-4" />}
              className="w-full sm:w-auto"
            >
              บันทึกและกระจายไปยังทุกเครื่อง
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
